import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Calendar, ArrowRightLeft, XCircle, AlertCircle, Loader, Phone } from 'lucide-react';
import { appointmentService } from '../../services/appointmentService';
import { bailAgentService } from '../../services/bailAgentService';
import { emailService } from '../../services/emailService';
import { supabase } from '../../lib/supabase';

export default function AppointmentManagementDashboard() {
  const [appointments, setAppointments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reassigningId, setReassigningId] = useState(null);
  const [selectedNewAgent, setSelectedNewAgent] = useState('');
  const [reassignmentReason, setReassignmentReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadDashboardData();
    
    // Check if there's a token query parameter for approval action
    const urlParams = new URLSearchParams(window?.location?.search);
    const token = urlParams?.get('token');
    
    if (token) {
      handleTokenApproval(token);
    }
  }, []);

  const handleTokenApproval = async (token) => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors

      // Find appointment by token
      const { data: appointment, error: fetchError } = await supabase
         .from('appointments')
         .select('*')
         .eq('approval_token', token)
         .single();

      if (fetchError || !appointment) {
        setError('Appointment not found or link has expired.');
        return;
      }

      if (appointment?.status !== 'pending') {
        setError(`This appointment has already been ${appointment?.status}.`);
        await loadDashboardData();
        return;
      }

      // Update appointment status to approved
      const { error: updateError } = await supabase
         .from('appointments')
         .update({
          status: 'approved',
          approved_at: new Date()?.toISOString(),
          approved_by_email: 'agent@sailorsbail.com',
          updated_at: new Date()?.toISOString()
        })
         .eq('id', appointment?.id);

      if (updateError) {
        throw new Error(`Failed to approve appointment: ${updateError?.message}`);
      }

      // Reload dashboard data to get updated appointments
      await loadDashboardData();
      
      // Remove token from URL to prevent re-processing
      window?.history?.replaceState({}, document?.title, window?.location?.pathname);

      // Show success message after everything is loaded
      alert('✅ Appointment approved successfully!');

    } catch (err) {
      console.error('Token approval error:', err);
      setError(err?.message || 'Failed to approve appointment via link');
    } finally {
      // Ensure loading state is always cleared
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      // Don't set loading here if called from handleTokenApproval
      // Only set it for direct calls
      if (!window?.location?.search?.includes('token=')) {
        setLoading(true);
      }
      setError('');

      // Load all appointments - FIXED: properly destructure the response
      const appointmentsResponse = await appointmentService?.getAllAppointments();
      
      if (appointmentsResponse?.error) {
        throw new Error(appointmentsResponse?.error);
      }

      // Load all team members
      const { data: agentsData, error: agentsError } = 
        await bailAgentService?.getAllAgents();

      if (agentsError) {
        throw new Error(agentsError);
      }

      setAppointments(appointmentsResponse?.data || []);
      setTeamMembers(agentsData || []);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleReassignAppointment = async (appointmentId) => {
    if (!selectedNewAgent || !reassignmentReason?.trim()) {
      alert('Please select a new team member and provide a reason for reassignment');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Get appointment details
      const appointment = appointments?.find(a => a?.id === appointmentId);
      if (!appointment) {
        throw new Error('Appointment not found');
      }

      // Get agent details
      const originalAgent = teamMembers?.find(a => a?.id === appointment?.bail_agent_id);
      const newAgent = teamMembers?.find(a => a?.id === selectedNewAgent);

      if (!originalAgent || !newAgent) {
        throw new Error('Agent information not found');
      }

      // Call reassignment function
      const { data, error: reassignError } = await supabase
         .rpc('reassign_appointment', {
          p_appointment_id: appointmentId,
          p_new_agent_id: selectedNewAgent,
          p_reassigned_by_email: 'admin@sailorsbail.com', // TODO: Get from auth context
          p_reason: reassignmentReason
        });

      if (reassignError) {
        throw new Error(`Reassignment failed: ${reassignError?.message}`);
      }

      // Send reassignment email to client
      const appointmentTime = new Date(appointment?.appointment_date)?.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      await emailService?.sendAppointmentReassignment({
        clientName: appointment?.client_name,
        clientEmail: appointment?.client_email,
        originalAgentName: originalAgent?.full_name,
        newAgentName: newAgent?.full_name,
        appointmentDate: appointment?.appointment_date,
        appointmentTime,
        purpose: appointment?.purpose,
        reassignmentReason,
        approvalToken: appointment?.approval_token,
        appointmentId: appointment?.id
      });

      // Reset form and reload data
      setReassigningId(null);
      setSelectedNewAgent('');
      setReassignmentReason('');
      await loadDashboardData();

      alert('Appointment reassigned successfully! Client has been notified via email.');
    } catch (err) {
      console.error('Error reassigning appointment:', err);
      setError(err?.message || 'Failed to reassign appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const appointment = appointments?.find(a => a?.id === appointmentId);
      if (!appointment) {
        throw new Error('Appointment not found');
      }

      const agent = teamMembers?.find(a => a?.id === appointment?.bail_agent_id);

      // Update appointment status
      const { error: updateError } = await supabase
         .from('appointments')
         .update({ status: 'cancelled', declined_reason: 'Cancelled by team member' })
         .eq('id', appointmentId);

      if (updateError) {
        throw new Error(`Failed to cancel appointment: ${updateError?.message}`);
      }

      // Send cancellation email
      const appointmentTime = new Date(appointment?.appointment_date)?.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      await emailService?.sendAppointmentCancellation({
        clientName: appointment?.client_name,
        clientEmail: appointment?.client_email,
        agentName: agent?.full_name || 'Our Team',
        appointmentDate: appointment?.appointment_date,
        appointmentTime,
        purpose: appointment?.purpose,
        cancellationReason: 'Cancelled by team member'
      });

      await loadDashboardData();
      alert('Appointment cancelled successfully! Client has been notified via email.');
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setError(err?.message || 'Failed to cancel appointment');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAppointments = () => {
    if (filterStatus === 'all') return appointments;
    return appointments?.filter(a => a?.status === filterStatus);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      declined: { bg: 'bg-red-100', text: 'text-red-800', label: 'Declined' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Completed' },
      pending_client_approval: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Pending Client' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  if (loading && appointments?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Appointment Management Dashboard - Sailors Bail Bonds</title>
        <meta name="description" content="Manage and reassign client appointments" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Appointment Management</h1>
                  <p className="text-sm text-gray-600">Manage and reassign client appointments</p>
                </div>
              </div>
              <a
                href="tel:+19162563468"
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Emergency: (916) 256-3468
              </a>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="max-w-7xl mx-auto mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="max-w-7xl mx-auto mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filter:</span>
              {['all', 'pending', 'pending_client_approval', 'approved', 'cancelled']?.map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
  	filterStatus === status
    		? 'bg-blue-600 text-white'
    		: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
	}`}
                >
                  {status?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="max-w-7xl mx-auto">
          <div className="grid gap-6">
            {getFilteredAppointments()?.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No appointments found</h3>
                <p className="text-gray-600">
                  {filterStatus === 'all' ?'There are no appointments scheduled yet.'
                    : `There are no ${filterStatus} appointments.`}
                </p>
              </div>
            ) : (
              getFilteredAppointments()?.map(appointment => {
                const agent = teamMembers?.find(a => a?.id === appointment?.bail_agent_id);
                const isReassigning = reassigningId === appointment?.id;

                return (
                  <div key={appointment?.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {appointment?.client_name}
                            </h3>
                            {getStatusBadge(appointment?.status)}
                          </div>
                          {/* Confirmation Number */}
                          {appointment?.confirmation_number && (
                            <div className="mb-3 inline-block px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg">
                              <span className="text-xs font-semibold text-blue-700">Confirmation #: {appointment?.confirmation_number}</span>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Email:</span>
                              <span className="ml-2 text-gray-900">{appointment?.client_email}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Phone:</span>
                              <span className="ml-2 text-gray-900">{appointment?.client_phone}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Date:</span>
                              <span className="ml-2 text-gray-900">
                                {new Date(appointment?.appointment_date)?.toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Time:</span>
                              <span className="ml-2 text-gray-900">
                                {new Date(appointment?.appointment_date)?.toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Agent:</span>
                              <span className="ml-2 text-gray-900">{agent?.full_name || 'Unknown'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Purpose:</span>
                              <span className="ml-2 text-gray-900">
                                {appointment?.purpose?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                              </span>
                            </div>
                          </div>
                          {appointment?.purpose_details && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700">{appointment?.purpose_details}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Reassignment Form */}
                      {isReassigning ? (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="font-semibold text-gray-900 mb-3">Reassign Appointment</h4>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                New Team Member
                              </label>
                              <select
                                value={selectedNewAgent}
                                onChange={(e) => setSelectedNewAgent(e?.target?.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="">Select team member...</option>
                                {teamMembers
                                   .filter(tm => tm?.id !== appointment?.bail_agent_id)
                                   .map(tm => (
                                    <option key={tm?.id} value={tm?.id}>
                                      {tm?.full_name} - {tm?.specializations?.[0] || 'Bail Agent'}
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reason for Reassignment
                              </label>
                              <textarea
                                value={reassignmentReason}
                                onChange={(e) => setReassignmentReason(e?.target?.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Explain why this appointment needs to be reassigned..."
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleReassignAppointment(appointment?.id)}
                                disabled={loading}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                              >
                                Confirm Reassignment
                              </button>
                              <button
                                onClick={() => {
                                  setReassigningId(null);
                                  setSelectedNewAgent('');
                                  setReassignmentReason('');
                                }}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={() => setReassigningId(appointment?.id)}
                            disabled={appointment?.status !== 'pending'}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                            Reassign
                          </button>
                          <button
                            onClick={() => handleCancelAppointment(appointment?.id)}
                            disabled={['cancelled', 'completed']?.includes(appointment?.status)}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <XCircle className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}
